/**
 * Stable E2E test IDs — single source of truth shared between screen components
 * and the Playwright test helpers.
 *
 * RULE: every element that an E2E test touches must have a TEST_IDS entry.
 * Copy, labels, and icons can change freely — this contract does not.
 *
 * Usage in screens:
 *   import { TEST_IDS } from "../testing/testIds";
 *   <button data-testid={TEST_IDS.COMPLETION_REFLECT_BTN} ...>
 *
 * Usage in test helpers:
 *   import { TEST_IDS } from "../../../../src/testing/testIds";
 *   await page.getByTestId(TEST_IDS.COMPLETION_REFLECT_BTN).click();
 */
import type { WakeOffset } from "../constants";

/**
 * URL param value that activates the LegacyRetreatBridge engine test harness.
 * Used as ?ui=<E2E_HARNESS_UI_KEY>. Opaque random token — intentionally
 * unguessable so no production user can stumble onto this route.
 *
 * Single source of truth — imported by both App.tsx and tests/e2e/constants.ts.
 *
 * TODO: remove once arch-zombie-session + session-auto-complete E2E suites
 * are migrated to /demo. See App.tsx for the matching TODO.
 */
export const E2E_HARNESS_UI_KEY = "7c4f1a3e9b2d4e8fa0513c6b9d2f8e17";

export const TEST_IDS = {
  // ── Screens ────────────────────────────────────────────────────────────────
  HOME_SCREEN:           "home-screen",
  INTENTION_SCREEN:      "intention-screen",
  PREPARATION_SCREEN:    "preparation-screen",
  LIVE_SCREEN:           "live-screen",
  END_INTENTION_SCREEN:  "end-intention-screen",
  COMPLETION_SCREEN:     "completion-screen",
  REFLECTION_SCREEN:     "reflection-screen",
  RETURNING_SCREEN:      "returning-screen",
  HISTORY_SCREEN:             "history-screen",
  SESSION_DETAIL_REFLECTION:  "session-detail-reflection",
  LIBRARY_SCREEN:        "library-screen",
  RETREAT_DETAIL_SCREEN:          "retreat-detail-screen",
  RETREAT_DETAIL_MULTIDAY_NOTE:   "retreat-detail-multiday-note",
  RETREAT_DETAIL_SIGNALS:         "retreat-detail-signals",
  RETREAT_DETAIL_SIGNAL_BTN:      "retreat-detail-signal-btn",
  SCHEDULING_SCREEN:     "scheduling-screen",
  SETTINGS_SCREEN:       "settings-screen",
  BELL_LIBRARY_SCREEN:   "bell-library-screen",

  // ── MetaStrip ──────────────────────────────────────────────────────────────
  BACK_BTN:             "meta-strip-back-btn",

  // ── Shell ──────────────────────────────────────────────────────────────────
  TAB_BAR:              "tab-bar",
  TAB_HOME:             "tab-home",
  TAB_DISCOVER:         "tab-discover",
  TAB_HISTORY:          "tab-history",
  CONNECTIVITY_BANNER:  "connectivity-banner",
  MISSED_BELL_BANNER:   "missed-bell-banner",
  PUSH_ALARM_UNCONFIRMED_BANNER: "push-alarm-unconfirmed-banner",

  // ── IntentStrip ────────────────────────────────────────────────────────────
  INTENT_STRIP:         "intent-strip",

  // ── HomeScreen ─────────────────────────────────────────────────────────────
  HOME_SETTINGS_BTN:    "open-settings",
  BROWSE_ALL_CTA:       "browse-all-cta",
  QUICK_START_DEMO:     "quick-start-demo",
  HOME_INSTALL_BTN:     "home-install-btn",
  HOME_RESUME_CARD:     "home-resume-card",
  HOME_RESUME_BTN:      "home-resume-btn",
  HOME_RESUME_END_BTN:  "home-resume-end-btn",
  HOME_TRUST_BADGE:     "trust-badge-card",

  // ── IntentionScreen ────────────────────────────────────────────────────────
  INTENTION_OPTION:      "intention-option",
  CONFIRM_INTENTION_CTA: "confirm-intention-cta",
  SKIP_INTENTION_CTA:    "skip-intention-cta",

  // ── PreparationScreen ──────────────────────────────────────────────────────
  PREPARATION_CONTINUE_BTN: "preparation-continue-btn",
  PREPARATION_SKIP_BTN:     "preparation-skip-btn",

  // ── EndWithIntentionScreen ─────────────────────────────────────────────────
  END_INTENTION_CONTINUE_CTA: "continue-cta",
  END_INTENTION_END_CTA:      "end-cta",

  // ── TrustScreen ────────────────────────────────────────────────────────────
  TRUST_CHECK_LIST:         "trust-check-list",
  TRUST_CONTINUE_BTN:       "trust-continue-btn",
  TRUST_EXPLAIN_TOGGLE:     "trust-explain-toggle",
  TRUST_EXPLANATIONS:       "trust-explanations",

  // ── CompletionScreen ───────────────────────────────────────────────────────
  COMPLETION_BELL_BTN:    "completion-bell-btn",
  COMPLETION_REFLECT_BTN:   "completion-reflect-btn",
  COMPLETION_CLOSE_BTN:     "completion-close-btn",
  COMPLETION_FEEDBACK_BTN:  "completion-feedback-btn",

  // ── ReflectionScreen ───────────────────────────────────────────────────────

  // ── ReturningScreen ────────────────────────────────────────────────────────
  RETURNING_RETURN_BTN: "returning-return-btn",
  RETURNING_END_BTN:    "returning-end-btn",

  // ── RetreatDetailScreen ────────────────────────────────────────────────────
  DETAIL_START_NOW_BTN: "detail-start-now-btn",
  DETAIL_SCHEDULE_BTN:  "detail-schedule-btn",

  // ── SchedulingScreen ───────────────────────────────────────────────────────
  SCHEDULING_DATE_INPUT:    "scheduling-date-input",
  SCHEDULING_TIME_INPUT:    "scheduling-time-input",
  SCHEDULING_CONFIRM_BTN:   "scheduling-confirm-btn",

  // ── HistoryScreen ──────────────────────────────────────────────────────────
  HISTORY_EMPTY_STATE:        "history-empty-state",
  HISTORY_SESSION_CARD:       "history-session-card",
  HISTORY_SETTINGS_GEAR:      "history-settings-gear",
  HISTORY_HEATMAP:            "history-heatmap",
  HISTORY_YEAR_GROUP:         "history-year-group",
  HISTORY_MONTH_GROUP:        "history-month-group",
  HISTORY_SEGMENT_ALL:        "history-segment-all",
  HISTORY_SEGMENT_COMPLETED:  "history-segment-completed",
  HISTORY_SEGMENT_ATTEMPTED:  "history-segment-attempted",
  HISTORY_STAT_COMPLETED:     "history-stat-completed",
  HISTORY_STAT_LONGEST:       "history-stat-longest",

  // ── SessionDetailScreen ──────────────────────────────────────────────────────
  SESSION_DETAIL_SCREEN:      "session-detail-screen",

  // ── Engine test harness buttons (LegacyRetreatBridge, ?ui=E2E_HARNESS_UI_KEY) ─
  // TODO: remove when arch-zombie-session + session-auto-complete E2E suites are
  // migrated to /demo. See App.tsx for the matching TODO.
  BEGIN_RETREAT_BTN:      "begin-retreat-btn",
  COMPLETE_RETREAT_BTN:   "complete-retreat-btn",
  CONFIRM_COMPLETE_BTN:   "confirm-complete-btn",
  CANCEL_COMPLETE_BTN:    "cancel-complete-btn",
  YES_CONTINUE_BTN:       "yes-continue-btn",
  START_FRESH_BTN:        "start-fresh-btn",

  // ── Auth Screens ───────────────────────────────────────────────────────────
  HOLD_TO_BEGIN_BTN:      "hold-to-begin-btn",
  SIGN_IN_BTN:            "sign-in-btn",

  // ── Landing Screens ────────────────────────────────────────────────────────
  LANDING_HERO:           "landing-hero",
  LANDING_CTA_BTN:        "landing-cta-btn",
  LANDING_NAV:            "landing-nav",

  // ── Additional Screens ─────────────────────────────────────────────────────
  PAYMENT_SUCCESS_SCREEN:     "payment-success-screen",
  PAYMENT_SUCCESS_ACTIVATED:  "payment-success-screen-activated",
  PAYMENT_SUCCESS_TIMEOUT:    "payment-success-screen-timeout",
  PAYMENT_REQUIRED_SCREEN:    "payment-required-screen",
  PAYMENT_CODE_INPUT:         "payment-code-input",
  PAYMENT_SUBMIT_BTN:         "payment-submit-btn",
  PAYMENT_ERROR_MSG:          "payment-error-msg",
  PAYMENT_WAITLIST_LINK:      "payment-waitlist-link",
  PAYMENT_CONTACT_LINK:       "payment-contact-link",
  MISSED_SCREEN:              "missed-screen",
  SELECTION_SCREEN:           "selection-screen",
  SIGNIN_SCREEN:              "signin-screen",
  SIGNIN_SCREEN_SENT:         "signin-screen-sent",
  INSTALL_PROMPT_SCREEN:      "install-prompt-screen",
  INSTALL_BRIDGE_SCREEN:      "install-bridge-screen",
  RESONANCE_MOMENT_SCREEN:    "transition-bell",

  // ── LiveScreen ─────────────────────────────────────────────────────────────
  LIVE_TIMER:                 "live-timer",
  LIVE_PROGRESS_BAR:          "live-progress-bar",
  LIVE_PROGRESS_FILL:         "live-progress-fill",
  LIVE_SCHEDULE_SHEET:        "live-schedule-sheet",
  LIVE_BELL_TRANSITION:       "live-bell-transition",
  LIVE_HOLD_OVERLAY:          "live-hold-overlay",
  LIVE_HOLD_TUTORIAL:         "live-hold-tutorial",
  LIVE_PAUSE_NOTE:            "live-pause-note",
  LIVE_SCHEDULE_HINT:         "live-schedule-hint",
  LIVE_AUDIO_RECOVERY:        "live-audio-recovery",

  // ── EndWithIntentionScreen (v2) ────────────────────────────────────────────
  END_INTENTION_HAIRLINE:     "end-intention-hairline",
  END_INTENTION_ECHO:         "end-intention-echo",
  END_INTENTION_TIME_CARD:    "end-intention-time-card",

  // ── ReflectionScreen (diary) ───────────────────────────────────────────────
  REFLECTION_TEXTAREA:        "reflection-diary-textarea",
  REFLECTION_WORD_COUNT:      "reflection-word-count",
  REFLECTION_SAVE_DIARY:      "reflection-save-diary",
  REFLECTION_CLOSE:           "reflection-close",
  REFLECTION_VOICE_TILE:  "reflection-voice-tile",
  REFLECTION_PHOTO_TILE:  "reflection-photo-tile",

  // ── Discover (LibraryScreen v2) ────────────────────────────────────────────
  DISCOVER_SEARCH:            "discover-search",
  DISCOVER_SEARCH_CLEAR:      "discover-search-clear",
  DISCOVER_TRAD_RAIL:         "discover-tradition-rail",
  DISCOVER_FACETS:            "discover-facets",
  DISCOVER_COUNT:             "discover-count",
  DISCOVER_CARDS:             "discover-cards",
  DISCOVER_CARD:              "discover-card",
  DISCOVER_EMPTY:             "discover-empty",
  DISCOVER_MINE_PILL:         "discover-mine-pill",
  DISCOVER_MINE_SECTION:      "discover-mine-section",
  DISCOVER_MINE_CARD:         "discover-mine-card",
  DISCOVER_MINE_EDIT_BTN:     "discover-mine-edit-btn",
  DISCOVER_MINE_REMOVE_BTN:   "discover-mine-remove-btn",
  DISCOVER_MINE_NEW_BTN:      "discover-mine-new-btn",
  DISCOVER_MINE_EMPTY:        "discover-mine-empty",

  // ── TeachingScreen ─────────────────────────────────────────────────────────
  TEACHING_SCREEN:            "teaching-screen",
  TEACHING_BELL_CARD:         "teaching-bell-card",
  TEACHING_PLAY_BTN:          "teaching-play-btn",
  TEACHING_DONE_BTN:          "teaching-done-btn",
  TEACHING_SKIP_BTN:          "teaching-skip-btn",
  TEACHING_EMPTY:             "teaching-empty",

  // ── EndConfirmScreen ───────────────────────────────────────────────────────
  END_CONFIRM_SCREEN:         "end-confirm-screen",
  END_CONFIRM_RETURN_BTN:     "end-confirm-return-btn",

  // ── FeedbackScreen ─────────────────────────────────────────────────────────
  FEEDBACK_SCREEN:     "feedback-screen",
  FEEDBACK_SUBMIT_BTN: "feedback-submit-btn",
  FEEDBACK_CANCEL_BTN: "feedback-cancel-btn",

  // ── Feedback banner (HomeScreen) ──────────────────────────────────────────
  HOME_FEEDBACK_BANNER:         "home-feedback-banner",
  HOME_FEEDBACK_BANNER_DISMISS: "home-feedback-banner-dismiss",
  HOME_FEEDBACK_BANNER_OPEN:    "home-feedback-banner-open",

  // ── HomeScheduledScreen ────────────────────────────────────────────────────
  HOME_SCHEDULED_BEGIN_BTN:   "home-scheduled-begin-btn",
  HOME_SCHEDULED_CANCEL_BTN:  "home-scheduled-cancel-btn",

  // ── WaitingScreen ──────────────────────────────────────────────────────────
  WAITING_CANCEL_BTN:         "waiting-cancel-btn",

  // ── SelectionScreen ────────────────────────────────────────────────────────
  SELECTION_BROWSE_ALL_BTN:   "selection-browse-all-btn",

  // ── MissedScreen ───────────────────────────────────────────────────────────
  MISSED_RESCHEDULE_BTN:      "missed-reschedule-btn",
  MISSED_RETURN_BTN:          "missed-return-btn",

  // ── PaymentSuccessScreen ───────────────────────────────────────────────────
  PAYMENT_SUCCESS_REACH_OUT_BTN: "payment-success-reach-out-btn",

  // ── InstallPromptScreen ────────────────────────────────────────────────────
  INSTALL_PROMPT_DONE_BTN:    "install-prompt-done-btn",

  // ── InstallBridgeScreen ────────────────────────────────────────────────────
  INSTALL_BRIDGE_INSTALL_BTN: "install-bridge-install-btn",
  INSTALL_BRIDGE_SKIP_BTN:    "install-bridge-skip-btn",

  // ── PermissionsScreen ──────────────────────────────────────────────────────
  PERMISSIONS_SCREEN:         "permissions-screen",
  PERMISSIONS_GRANT_BTN:      "permissions-grant-btn",
  PERMISSIONS_SKIP_BTN:       "permissions-skip-btn",

  // ── BellPreviewScreen ──────────────────────────────────────────────────────
  BELL_PREVIEW_SET_DEFAULT_BTN: "bell-preview-set-default-btn",

  // ── What's New ─────────────────────────────────────────────────────────────
  WHATS_NEW_SCREEN: 'whats-new-screen',

  // ── SettingsScreen rows ────────────────────────────────────────────────────
  SETTINGS_BELL_STYLE_ROW:    "settings-bell-style-row",
  SETTINGS_TRADITION_ROW:     "settings-tradition-row",
  SETTINGS_SIGN_OUT_BTN:      "settings-sign-out-btn",
  SETTINGS_WHATS_NEW_ROW:     "settings-whats-new-row",

  // ── AdminScreen ────────────────────────────────────────────────────────────
  ADMIN_SCREEN:               "admin-screen",
  ADMIN_WAVE_TABLE:           "admin-wave-table",
  ADMIN_FEEDBACK_LIST:        "admin-feedback-list",
  ADMIN_FEEDBACK_CARD:        "admin-feedback-card",

  // ── Utility Elements ───────────────────────────────────────────────────────
  ELAPSED_TIMER:              "elapsed-timer",
  SILENT_AUDIO:               "silent-audio-bridge",
  SW_UPDATE_BANNER:           "sw-update-banner",
  SW_UPDATE_RELOAD_BTN:       "sw-update-reload-btn",

  // ── RetreatDetailScreen (dynamic) ─────────────────────────────────────────
  RETREAT_DETAIL_WARNING_BANNER:  "retreat-detail-warning-banner",
  RETREAT_DETAIL_ADJUST_BTN:      "retreat-detail-adjust-btn",
  RETREAT_DETAIL_NOBLE_SILENCE:   "retreat-detail-noble-silence",
  RETREAT_DETAIL_EDIT_BTN:        "retreat-detail-edit-btn",

  // ── ScheduleTimeline ──────────────────────────────────────────────────────
  SCHEDULE_TIMELINE:              "schedule-timeline",

  // ── AdjustPreferencesSheet ────────────────────────────────────────────────
  ADJUST_SHEET:                   "adjust-preferences-sheet",
  ADJUST_SHEET_START_TOMORROW:    "adjust-sheet-start-tomorrow",

  // ── ScheduleWizardScreen ──────────────────────────────────────────────────
  SCHEDULE_WIZARD_SCREEN:         "schedule-wizard-screen",
  SCHEDULE_WIZARD_TIMELINE:       "schedule-wizard-timeline",
  SCHEDULE_WIZARD_WARNING_BANNER: "schedule-wizard-warning-banner",
  SCHEDULE_WIZARD_ADJUST_BTN:     "schedule-wizard-adjust-btn",
  SCHEDULE_WIZARD_CONFIRM:        "schedule-wizard-confirm",

  // ── Whisper ────────────────────────────────────────────────────────────────
  WHISPER_LINE:                   "whisper-line",

  // ── ScheduleEditorScreen ────────────────────────────────────────────────────
  SCHEDULE_EDITOR_SCREEN:         "schedule-editor-screen",
  SCHEDULE_EDITOR_NAME_INPUT:     "schedule-editor-name-input",
  SCHEDULE_EDITOR_SAVE_BTN:       "schedule-editor-save-btn",
  SCHEDULE_EDITOR_CANCEL_BTN:     "schedule-editor-cancel-btn",
  SCHEDULE_EDITOR_CONTAINER_LIST: "schedule-editor-container-list",
  SCHEDULE_EDITOR_ADD_BTN:        "schedule-editor-add-btn",
  SCHEDULE_EDITOR_SAVE_ERROR:     "schedule-editor-save-error",
} as const;

export type TestId = (typeof TEST_IDS)[keyof typeof TEST_IDS];

/**
 * Type-safe testid helper. Rejects string literals that don't match a TEST_IDS value.
 * Use: data-testid={tid(TEST_IDS.HOME_SCREEN)}
 * Typos like tid("home-scrren") are caught by TypeScript, not at runtime.
 */
export const tid = (id: TestId): string => id;

/** Dynamic testid for wake-offset pill buttons. */
export const HOME_WAKE_OFFSET_ID = (v: WakeOffset): string => `wake-offset-${v}`;
